import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Container, Button, Dropdown, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  SortableTable,
  type ColumnRenderItem,
} from '../../common/components/SortableTable';
import {
  PaginationComponent,
  SearchBar,
  SectionHeader,
  FileSelectionModal,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getInvoicesAsync,
  exportInvoicesExcelAsync,
  recreateInvoicePdfAsync,
  importOpenBalanceAsync,
  getAvailableImportFilesAsync,
  pollImportJobStatusAsync,
  invoiceFilter,
  selectInvoiceItems,
  selectInvoiceItemsLoadingStatus,
  selectInvoicePagination,
  selectAvailableImportFiles,
  selectAvailableFilesLoadingStatus,
  selectImportJobStatus,
  setSearchFilters,
  resetImportJobStatus,
} from './invoicesSlice';
import { LOADING_STATUS } from '../../common/constants';
import {
  CreateBookingPdf,
  getInvoiceDetailsPdfAsync,
} from '../bookings/bookingsSlice';
import 'react-datepicker/dist/react-datepicker.css';

function Invoices() {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();

  const [invoiceTableItems, setInvoiceTableItems] = useState([]);
  const [showFileSelectionModal, setShowFileSelectionModal] = useState(false);
  const [pollingIntervalId, setPollingIntervalId] =
    useState<NodeJS.Timeout | null>(null);
  const invoiceItems = useAppSelector(selectInvoiceItems);
  const invoiceItemsStatus = useAppSelector(selectInvoiceItemsLoadingStatus);
  const invoicePagination = useAppSelector(selectInvoicePagination);
  const availableFiles = useAppSelector(selectAvailableImportFiles);
  const availableFilesStatus = useAppSelector(
    selectAvailableFilesLoadingStatus,
  );
  const importJobStatus = useAppSelector(selectImportJobStatus);
  const [searchFilters, setLocalSearchFilters] = useState<invoiceFilter>({
    PageNumber: 1,
    PageSize: 10,
  });

  const getPdfInvoice = (
    invoiceId: string,
    copy: boolean,
    compensation: boolean,
    locale: string,
    advance: boolean,
  ) => {
    const invoiceCommand = {
      invoiceId,
      copy,
      compensation,
      locale,
      advance,
    } as CreateBookingPdf;
    dispatch(getInvoiceDetailsPdfAsync(invoiceCommand));
  };

  const handleFilterChange = (field: string, value: unknown) => {
    const formattedValue =
      value instanceof Date ? format(value, 'yyyy-MM-dd') : value;
    const updated = { ...searchFilters, [field]: formattedValue };
    setLocalSearchFilters(updated);
    dispatch(setSearchFilters(updated));
  };

  const doSearch = async () => {
    setInvoiceTableItems([]);
    const searchWithPageOne = { ...searchFilters, PageNumber: 1 };
    setLocalSearchFilters(searchWithPageOne);
    dispatch(setSearchFilters(searchWithPageOne));
    await dispatch(getInvoicesAsync(searchWithPageOne));
  };

  const handleExcelExport = () => {
    dispatch(exportInvoicesExcelAsync(searchFilters));
  };

  const handleImportOpenBalance = async () => {
    dispatch(resetImportJobStatus());
    setShowFileSelectionModal(true);
    await dispatch(getAvailableImportFilesAsync());
  };

  const handleConfirmFileSelection = async (fileName: string) => {
    // Reset previous import status before starting new import
    dispatch(resetImportJobStatus());

    const { payload } = await dispatch(importOpenBalanceAsync(fileName));

    if (payload?.result?.jobId) {
      const { jobId } = payload.result;

      // Start polling for job status
      const intervalId = setInterval(() => {
        dispatch(pollImportJobStatusAsync(jobId));
      }, 2000); // Poll every 2 seconds

      setPollingIntervalId(intervalId);
    }
  };

  // Cleanup polling when import completes
  useEffect(() => {
    const isCompleted = importJobStatus?.status === 'Completed';
    const isFailed = importJobStatus?.status === 'Failed';

    if (isCompleted || isFailed) {
      // Stop polling
      if (pollingIntervalId) {
        clearInterval(pollingIntervalId);
        setPollingIntervalId(null);
      }

      // Close modal when completed
      if (isCompleted) {
        setTimeout(() => {
          setShowFileSelectionModal(false);
        }, 1500);
      }
    }
  }, [importJobStatus?.status, pollingIntervalId]);

  const handleRecreateInvoicePdf = (invoiceId: string) => {
    dispatch(recreateInvoicePdfAsync({ invoiceId, locale: 'fi' }));
  };

  const handlePaginationClick = (page: number) => {
    const updatedFilters = { ...searchFilters, PageNumber: page };
    setLocalSearchFilters(updatedFilters);
    dispatch(setSearchFilters(updatedFilters));
    dispatch(getInvoicesAsync(updatedFilters));
  };

  const renderOpenBalance = (value: number | undefined) => {
    if (value === undefined || value === null) {
      return (
        <span style={{ color: '#999', fontStyle: 'italic' }}>Ei dataa</span>
      );
    }
    if (value === 0) {
      return (
        <span style={{ color: '#28a745', fontWeight: 'bold' }}>Maksettu</span>
      );
    }
    if (value > 0) {
      return (
        <span style={{ color: 'red', fontWeight: 'bold' }}>
          {value.toLocaleString('fi-FI', {
            style: 'currency',
            currency: 'EUR',
          })}
        </span>
      );
    }
    return (
      <span style={{ color: '#666' }}>
        {value.toLocaleString('fi-FI', {
          style: 'currency',
          currency: 'EUR',
        })}
      </span>
    );
  };

  useEffect(() => {
    if (invoiceItems && invoiceItems.length > 0) {
      const formattedInvoiceItemsList = invoiceItems?.map(item => {
        const dueDate = item.dueDate ? new Date(item.dueDate) : null;
        const isDueDateGone = dueDate ? dueDate < new Date() : false;
        return {
          id: item.id,
          Laskunumero: item.number,
          LaskunPäivä: item.invoiceDate,
          LaskunSumma: item.totalAmount,
          Eräpäivä: item.dueDate,
          MaksajanNimi: item.payerName,
          Varausnumero: item.bookingNumber,
          AvoinSaldo: item.openBalance,
          compensation: item.discriminator
            .toLowerCase()
            .includes('compensation'),
          Advance: item.isAdvance,
          isDueDateGone,
        };
      });
      setInvoiceTableItems(formattedInvoiceItemsList);
    }
  }, [invoiceItems]);

  const totalAmount = invoiceItems.reduce(
    (sum, item) => sum + (item.totalAmount || 0),
    0,
  );

  const renderTable: ColumnRenderItem[] = [
    {
      column: 'Laskunumero',
      render: (value: string | number) => (
        <div>
          <span>{value}</span>
        </div>
      ),
    },
    {
      column: 'LaskunPäivä',
      render: (value: string) => (
        <div>
          <span>
            {value ? new Date(value).toLocaleDateString('fi-FI') : ''}
          </span>
        </div>
      ),
    },
    {
      column: 'LaskunSumma',
      render: (value: number) => (
        <div>
          <span>
            {value
              ? value.toLocaleString('fi-FI', {
                  style: 'currency',
                  currency: 'EUR',
                })
              : '€0.00'}
          </span>
        </div>
      ),
    },
    {
      column: 'Eräpäivä',
      render: (value: string) => (
        <div>
          <span>
            {value ? new Date(value).toLocaleDateString('fi-FI') : ''}
          </span>
        </div>
      ),
    },
    {
      column: 'AvoinSaldo',
      render: (value: number | undefined) => (
        <div>{renderOpenBalance(value)}</div>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Laskut - Haku</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader Breadcrumbs={['Laskuarkisto']} Title="Laskut" />

        <SearchBar
          FreeTextOnChangeHandler={e =>
            handleFilterChange('searchFilter', e.target.value)
          }
          FreeTextPlaceholder="Pika-haku laskunumerolla"
          SearchHandler={doSearch}
          IsLoading={invoiceItemsStatus === LOADING_STATUS.LOADING}
          Value={searchFilters.searchFilter || ''}
          ShowResetButton
          ResetHandler={() => {
            const empty = { PageNumber: 1, PageSize: 10 } as invoiceFilter;
            setLocalSearchFilters(empty);
            setInvoiceTableItems([]);
            dispatch(setSearchFilters(empty));
          }}
        >
          <Row className="mt-3">
            <Col md={6} lg={3} className="mb-3">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="invNumFrom" className="form-label d-block">
                Laskunumeroväli alkaen
              </label>
              <input
                id="invNumFrom"
                type="number"
                className="form-control w-100"
                value={searchFilters.invoiceNumberFrom || ''}
                onChange={e =>
                  handleFilterChange(
                    'invoiceNumberFrom',
                    e.target.value ? parseInt(e.target.value, 10) : undefined,
                  )
                }
              />
            </Col>
            <Col md={6} lg={3} className="mb-3">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="invNumTo" className="form-label d-block">
                Laskunumeroväli päättyen
              </label>
              <input
                id="invNumTo"
                type="number"
                className="form-control w-100"
                value={searchFilters.invoiceNumberTo || ''}
                onChange={e =>
                  handleFilterChange(
                    'invoiceNumberTo',
                    e.target.value ? parseInt(e.target.value, 10) : undefined,
                  )
                }
              />
            </Col>
            <Col md={6} lg={3} className="mb-3">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="dtFrom" className="form-label d-block">
                Päivämäärä alkaen
              </label>
              <DatePicker
                id="dtFrom"
                locale={i18n.language}
                selected={
                  searchFilters.dateFrom
                    ? new Date(searchFilters.dateFrom)
                    : null
                }
                onChange={date => handleFilterChange('dateFrom', date)}
                dateFormat="dd.MM.yyyy"
                showWeekNumbers
                isClearable
                placeholderText="Valitse päivä"
                className="form-control w-100"
                aria-label="Päivämäärä alkaen"
              />
            </Col>
            <Col md={6} lg={3} className="mb-3">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="dtTo" className="form-label d-block">
                Päivämäärä päättyen
              </label>
              <DatePicker
                id="dtTo"
                locale={i18n.language}
                selected={
                  searchFilters.dateTo ? new Date(searchFilters.dateTo) : null
                }
                onChange={date => handleFilterChange('dateTo', date)}
                minDate={
                  searchFilters.dateFrom
                    ? new Date(searchFilters.dateFrom)
                    : undefined
                }
                disabled={!searchFilters.dateFrom}
                dateFormat="dd.MM.yyyy"
                showWeekNumbers
                isClearable
                placeholderText="Valitse päivä"
                className="form-control w-100"
                aria-label="Päivämäärä päättyen"
              />
            </Col>
            <Col md={6} lg={3} className="mb-3">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="custName" className="form-label d-block">
                Asiakkaan nimi
              </label>
              <input
                id="custName"
                type="text"
                className="form-control w-100"
                placeholder="Hae asiakkaan nimellä"
                value={searchFilters.customerName || ''}
                onChange={e =>
                  handleFilterChange(
                    'customerName',
                    e.target.value || undefined,
                  )
                }
              />
            </Col>
            <Col md={6} lg={3} className="mb-3">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="bookNum" className="form-label d-block">
                Varausnumero
              </label>
              <input
                id="bookNum"
                type="number"
                className="form-control w-100"
                placeholder="Hae varausnumerolla"
                value={searchFilters.bookingNumberSearch || ''}
                onChange={e =>
                  handleFilterChange(
                    'bookingNumberSearch',
                    e.target.value ? parseInt(e.target.value, 10) : undefined,
                  )
                }
              />
            </Col>
            <Col md={6} lg={3} className="mb-3">
              <div className="form-check mt-4">
                <input
                  id="openBalanceOnly"
                  type="checkbox"
                  className="form-check-input"
                  checked={searchFilters.openBalanceOnly || false}
                  onChange={e =>
                    handleFilterChange('openBalanceOnly', e.target.checked)
                  }
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="openBalanceOnly" className="form-check-label">
                  Vain avoimen saldon laskut
                </label>
              </div>
            </Col>
          </Row>
        </SearchBar>

        <div className="mt-3 d-flex gap-2 justify-content-between align-items-center">
          <div className="d-flex gap-2">
            <Button
              variant="secondary"
              onClick={handleExcelExport}
              disabled={invoiceItemsStatus === LOADING_STATUS.LOADING}
            >
              Vie Excel
            </Button>
            <Button
              variant="secondary"
              onClick={handleImportOpenBalance}
              disabled={invoiceItemsStatus === LOADING_STATUS.LOADING}
            >
              Tuo avoimet saldot
            </Button>
          </div>
        </div>

        <SortableTable
          data={invoiceTableItems}
          setData={setInvoiceTableItems}
          dateColumns={['LaskunPäivä', 'Eräpäivä']}
          noSortColumns={['id', 'Toiminnot']}
          columns={[
            { value: 'Laskunumero', label: 'Laskun numero' },
            { value: 'LaskunPäivä', label: 'Laskun päiväys' },
            { value: 'LaskunSumma', label: 'Laskun summa' },
            { value: 'Eräpäivä', label: 'Eräpäivä' },
            { value: 'AvoinSaldo', label: 'Avoin saldo' },
            { value: 'MaksajanNimi', label: 'Maksajan nimi' },
            { value: 'Varausnumero', label: 'Varausnumero' },
            { value: 'Toiminnot', label: 'Toiminnot' },
          ]}
          columnRender={
            [
              ...renderTable,
              {
                column: 'Varausnumero',
                render: (value: number | undefined) => (
                  <div>
                    <span>{value || '—'}</span>
                  </div>
                ),
              },
              {
                column: 'MaksajanNimi',
                render: (value: string | undefined) => (
                  <div>
                    <span>{value || '—'}</span>
                  </div>
                ),
              },
              {
                column: 'Toiminnot',
                render: (
                  value: string,
                  column: string,
                  columnIndex: number,
                  rowData: Record<string, unknown>,
                ) => {
                  const id = rowData.id as string;
                  return (
                    <div role="presentation" onClick={e => e.stopPropagation()}>
                      <Dropdown>
                        <Dropdown.Toggle
                          variant="link"
                          size="sm"
                          id={`dropdown-${id}`}
                        >
                          ⋮
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                          renderOnMount
                          popperConfig={{ strategy: 'fixed' }}
                        >
                          <Dropdown.Item
                            onClick={() => handleRecreateInvoicePdf(id)}
                          >
                            Luo PDF-lasku uudestaan
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  );
                },
              },
            ] as unknown as ColumnRenderItem[]
          }
          addProps={{
            tBodyRow: rowData => {
              const data = rowData as Record<string, unknown>;
              return {
                onClick: () =>
                  getPdfInvoice(
                    data.id as string,
                    false,
                    (data.compensation as boolean) || false,
                    'fi',
                    (data.Advance as boolean) || false,
                  ),
                style: { cursor: 'pointer' },
                className: (data.isDueDateGone as boolean)
                  ? 'bg-danger-subtle'
                  : '',
              };
            },
          }}
        />

        <div className="mt-3 d-flex justify-content-between">
          <span>Laskuja yhteensä: {invoicePagination.totalCount} kpl</span>
          <span>
            Summa yhteensä:{' '}
            {totalAmount.toLocaleString('fi-FI', {
              style: 'currency',
              currency: 'EUR',
            })}
          </span>
        </div>

        {invoicePagination.totalPages > 1 && (
          <PaginationComponent
            OnPageClick={handlePaginationClick}
            PaginationData={invoicePagination}
          />
        )}
      </Container>

      <FileSelectionModal
        ShowModal={showFileSelectionModal}
        HideModal={() => {
          setShowFileSelectionModal(false);
          if (pollingIntervalId) {
            clearInterval(pollingIntervalId);
            setPollingIntervalId(null);
          }
        }}
        ConfirmSelection={handleConfirmFileSelection}
        AvailableFiles={availableFiles}
        FilesLoading={availableFilesStatus === LOADING_STATUS.LOADING}
        IsLoading={invoiceItemsStatus === LOADING_STATUS.LOADING}
        ImportJobStatus={importJobStatus}
      />
    </>
  );
}

export default Invoices;
